import { useState, useEffect } from "react";
import { Info, CaretDown } from "phosphor-react";
import { Tooltip } from "react-tooltip";
import { cn } from "@/lib/utils";
import { VenueReview, VenueAccessibilityExperienceStats } from "common/types";
import { useI18N } from "common/hooks";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/shadcn/components/ui/dialog";
import { StandardButton } from "@/components/common/StandardButton";
import ExperienceCard from "./ExperienceCard";
import WideButton from "@/components/common/WideButton";
import ModalWithAction from "@/components/common/ModalWithAction";

// Progress Bar Component
interface ProgressBarProps {
  label: string;
  value: number;
  maxValue?: number;
}

const ProgressBar = ({ label, value, maxValue = 10 }: ProgressBarProps) => {
  const percentage = (value / maxValue) * 100;

  return (
    <div className="flex items-center gap-2 w-full">
      <div className="flex items-center min-w-[60px]">
        <span className="label-s-medium text-fg-neutral-strong">{label}</span>
      </div>
      <div className="flex flex-1 items-center gap-1">
        <div className="flex flex-1 items-center relative h-2">
          <div className="bg-container-neutral-minimal h-2 rounded-full w-full" />
          <div
            className="bg-fg-neutral-minimal h-2 rounded-full absolute top-0"
            style={{ width: `${percentage}%` }}
          />
        </div>
        <div className="flex items-center justify-end text-right min-w-[24px]">
          <span className="label-s-medium text-fg-neutral-strong">{value}</span>
        </div>
      </div>
    </div>
  );
};

// Divider Component
const Divider = () => (
  <div className="flex flex-col items-start justify-center w-full">
    <div className="h-px w-full bg-border-token-brand-minimal" />
  </div>
);


// Main ShareExperience Component
interface ShareExperienceProps {
  className?: string;
  venueAccessibilityExperienceStats?: VenueAccessibilityExperienceStats;
  totalCount?: number;
  currentSort?: string;
  onSortChange?: (sort: string) => void;  
  reviews?: VenueReview[];
  onFlag?: (review: VenueReview) => void;
}

export const ShareExperience = ({
  className,
  venueAccessibilityExperienceStats,
  totalCount = 10,
  currentSort = "Latest",
  onSortChange,
  reviews = [],
  onFlag,
}: ShareExperienceProps) => {
  const [sortBy, setSortBy] = useState(currentSort);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" ? window.innerWidth >= 768 : true
  );
  const i18n = useI18N();

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleSortChange = (value: string) => {
    setSortBy(value);
    onSortChange?.(value);
  };

  const coolPercentage = venueAccessibilityExperienceStats?.coolPercentage ?? 0;
  const weirdPercentage = venueAccessibilityExperienceStats?.weirdPercentage ?? 0;
  const coolCount = venueAccessibilityExperienceStats?.coolCount ?? 0;
  const weirdCount = venueAccessibilityExperienceStats?.weirdCount ?? 0;
  
  const totalFeedbackCount = coolCount + weirdCount;
  const hasEnoughReviews = reviews.length >= 3;
  
  // Determine which icon to show and the profile name
  const isSweet = coolPercentage >= weirdPercentage;
  const iconSrc = hasEnoughReviews 
    ? (isSweet ? "/images/Orange.svg" : "/images/OrangeSlice.svg")
    : "/images/Plant.svg";
  const profileName = hasEnoughReviews ? (isSweet ? "Sweet" : "Sour") : "--";

  return (
    <section
      className={cn(
        "flex flex-col gap-3 items-start w-full py-3",
        className
      )}
      aria-label="Share your experience"
    >
      {/* Header Section with Flavor Profile */}
      <header className="bg-surface-secondary flex flex-col gap-0 items-start p-6 w-full rounded-xl border border-border-token-brand-minimal">
        <div className="flex gap-4 items-start w-full">
          {/* Icon and Name */}
          <div className="flex flex-col items-center gap-0 shrink-0 w-[72px] pt-3">          
            
            <img src={iconSrc} alt={profileName} className="w-8 h-8" />

            <h2 className="title-l-bold text-fg-neutral-strong leading-8 -tracking-[0.48px] whitespace-nowrap">
              {profileName}
            </h2>
          </div>

          {/* Progress Bars */}
          <div className="flex flex-1 flex-col gap-1 items-start min-w-0">
            <div className="flex gap-2 items-center w-full">
              <span className="label-s-medium text-fg-neutral-strong">
                {hasEnoughReviews ? `${Math.round(coolPercentage)}% Sweetness` : i18n.get("stillTasting")}
              </span>
              <button
                id="sweetness-info-btn"
                type="button"
                className="w-8 h-8 flex items-center justify-center rounded hover:bg-bg-brand-ghost-hover transition-colors focus-blue"
                aria-label="Information about sweetness"
                onClick={() => { if (!isDesktop) setIsInfoOpen(true); }}
              >
                <Info size={20} className="text-fg-neutral-subtle" aria-hidden="true" />
              </button>
            </div>
            <ProgressBar
              label="Sweet"
              value={coolCount}
              maxValue={totalFeedbackCount || 1}
            />
            <ProgressBar
              label="Sour"
              value={weirdCount}
              maxValue={totalFeedbackCount || 1}
            />

            {!hasEnoughReviews && (
              <p className="label-s-regular text-fg-neutral-subtle pt-2">
                {i18n.get("averageRatingWillAppearAfter3Experiences")}
              </p>
            )}
          </div>
        </div>
      </header>

      {/* Controls Section */}
      <div className="flex items-center justify-between w-full">
        <span className="label-m-medium text-fg-neutral-strong text-center whitespace-nowrap">
          {totalCount} Experiences
        </span>
        <button
          type="button"
          className="bg-bg-brand-ghost-default flex gap-1 items-center justify-center min-h-8 min-w-8 pl-2 pr-1 py-1 rounded-lg hover:bg-bg-brand-ghost-hover active:bg-bg-brand-ghost-active transition-colors focus-blue"
          aria-label="Sort experiences"
          onClick={() => {
            // Toggle between Latest and Earliest
            const newSort = sortBy === "Latest" ? "Earliest" : "Latest";
            handleSortChange(newSort);
          }}
        >
          <span className="label-m-medium text-fg-neutral-strong text-center underline whitespace-nowrap">
            Date added ({sortBy === "Latest" ? "Newest" : "Oldest"})
          </span>
          <CaretDown size={20} className="text-fg-neutral-strong" />
        </button>
      </div>

      {/* Experience Cards List */}
      <div className="flex flex-col gap-6 items-start w-full pb-6">
        {reviews.map((review, index) => (
          <div key={index} className="w-full">
            <ExperienceCard 
              review={review} 
              onMenuClick={() => onFlag?.(review)}
            />
            {index < reviews.length - 1 && <Divider />}
          </div>
        ))}
      </div>

      {/* Mobile: Info dialog */}
      {!isDesktop && (    
      
        <Dialog open={isInfoOpen} onOpenChange={setIsInfoOpen}>
          <DialogContent
            style={{
              borderTopColor: "#352FC3",
              borderTopWidth: "10px",
              maxWidth: "600px",
            }}
            className="w-[80%] max-h-[90vh] rounded-[10px] px-[16px] py-[12px] flex flex-col overflow-y-auto overflow-x-hidden [&>button]:hidden"
          >
            <DialogHeader className="flex flex-row items-start space-y-0 text-left">
              <div className="w-8 h-8 flex-shrink-0">
                <Info size={20} className="text-fg-neutral-subtle" aria-hidden="true" />
              </div>
              <DialogTitle className="title-s-semibold flex-1 text-left">
                Sweet &amp; Sour User Ratings
              </DialogTitle>
            </DialogHeader>

            <DialogDescription className="label-m-medium text-fg-neutral-strong break-words py-1 text-left">
              Ratings are based on at least 3 experiences shared by users and
              aren&apos;t verified by Sociability.
              <br />
              Content may be reviewed or
              removed if it breaks our guidelines.
              <p className="pt-2">
                <a
                  href="https://www.sociability.app/accessibility-attitudes"
                  className="underline"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Learn more about how this works
                </a>
              </p>
            </DialogDescription>

            <DialogFooter>
              <WideButton
                className="w-full"
                onClick={() => setIsInfoOpen(false)}
              >
                Got it!
              </WideButton>
            </DialogFooter>

          </DialogContent>
        </Dialog>


      )}

      {/* Desktop: Rich tooltip */}
      {isDesktop && (
        <Tooltip
          anchorSelect="#sweetness-info-btn"
          place="right"
          clickable
          style={{ zIndex: 9999, maxWidth: "280px" }}
          render={() => (
            <div className="flex flex-col gap-2">
              <p className="label-m-medium break-words py-1 text-left">
                Sweet &amp; Sour user ratings are based on at least 3
                experiences shared by users and aren&apos;t verified by
                Sociability. Content may be reviewed or removed if it breaks
                our guidelines.
              </p>
              <a
                href="https://www.sociability.app/accessibility-attitudes"
                className="underline label-m-medium"
                target="_blank"
                rel="noopener noreferrer"
              >
                Learn more about how this works
              </a>
            </div>
          )}
        />
      )}
    </section>
  );
};

export default ShareExperience;
